import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useTheme } from '../../../contexts/ThemeContext';
import DarkModeWrapper from '../../../components/DarkModeWrapper';

const CommunityImpactSection = () => {
  const { isDark } = useTheme();

  const communityActivities = [
    {
      id: 1,
      title: "IEEE VOLUNTEER - NSBM GREEN UNIVERSITY",
      organization: "NSBM Green University",
      role: "IEEE Student Branch Member",
      duration: "June 2021 - Present",
      icon: "Users",
      color: "primary",
      image: "/assets/About/IEEE VOLUNTEER - NSBM GREEN UNIVERSITY.png",
      description: "'Road To Full Stack Mobile' organized by IEEE WIE student branch affinity group of NSBM - great opportunity for you to begin your journey of mobile application development.",
      impact: [
        "'Duothan' 2.0 organized by IEEE student branch of NSBM in collaboration with IEEE WIE student affinity group of NSBM successfully held on 23 February 2022",
        "'Arduino competition' for Arduino week celebration organized by IEEE student branch of NSBM in collaboration with EDSION on 15 March 2022",
        "'Duothan' 5.0 organized by IEEE student branch of NSBM in collaboration with IEEE WIE student affinity group of NSBM successfully held on 18 July 2025"
      ],
      achievements: [
        "Participated in mobile application development workshops",
        "Contributed to Arduino competition organization",
        "Active member in IEEE WIE collaboration events"
      ]
    },
    {
      id: 2,
      title: "CSSF & FOSS - NSBM GREEN UNIVERSITY",
      organization: "NSBM Green University",
      role: "Community Participant",
      duration: "June 2021 - Present",
      icon: "GitBranch",
      color: "success",
      image: "/assets/About/CSSF & FOSS - NSBM GREEN UNIVERSITY.png",
      description: "Active participant in FOSS (Free and Open Source Software) community and CSSF initiatives at NSBM Green University.",
      impact: [
        "'How to build your IT career during an economic crisis' great webinar organized by the FOSS Community of NSBM",
        "'ESTHETIQUE '21' the UI/UX workshop organized by FOSS NSBM together with WIF NSBM",
        "'HashCode' awareness session FOSS NSBM successfully held on 20 February 2022"
      ],
      achievements: [
        "Participated in IT career development webinars",
        "Attended UI/UX workshops and design sessions",
        "Engaged with FOSS community activities"
      ]
    },
    {
      id: 3,
      title: "Technical Content Creation",
      organization: "YouTube",
      role: "Full-Stack Development Educator",
      duration: "March 2022 - Present",
      icon: "GraduationCap",
      color: "brand-purple",
      image: "/assets/About/Technical Content Creation.png",
      description: "Creating educational content and project tutorials, sharing knowledge about full-stack development, software engineering concepts, and real-world project implementations.",
      impact: [
        "Created comprehensive tutorials on Full-Stack Online Job Portal System",
        "Developed educational content for POS Systems and Inventory Management",
        "Published Full-Stack Food Ordering App tutorials",
        "Shared real-world project implementations across multiple technologies"
      ],
      achievements: [
        "Building a community of developers through educational content",
        "Contributing to tech education ecosystem",
        "Covering technologies: React.js, Spring Boot, PHP, Flutter, and more"
      ]
    },
    {
      id: 4,
      title: "Community Development & Mentorship",
      organization: "Tech Community",
      role: "Community Builder",
      duration: "2021 - Present",
      icon: "Mic",
      color: "accent",
      image: "/assets/About/Community Development & Mentorship.png",
      description: "Actively engaged in tech community development, staying updated with latest industry trends and advancements through various community initiatives.",
      impact: [
        "Participated in multiple IEEE student branch activities",
        "Engaged with FOSS community initiatives",
        "Created educational content for developers",
        "Built network within Sri Lankan tech ecosystem"
      ],
      achievements: [
        "Active participant in tech workshops and webinars",
        "Contributed to mobile app and web development education",
        "Supported junior developers through tutorials and content"
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      primary: {
        bg: "bg-primary",
        text: "text-primary",
        light: "bg-primary/10",
        border: "border-primary/20"
      },
      success: {
        bg: "bg-success",
        text: "text-success",
        light: "bg-success/10",
        border: "border-success/20"
      },
      "brand-purple": {
        bg: "bg-brand-purple",
        text: "text-brand-purple",
        light: "bg-brand-purple/10",
        border: "border-brand-purple/20"
      },
      accent: {
        bg: "bg-accent",
        text: "text-accent",
        light: "bg-accent/10",
        border: "border-accent/20"
      }
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <DarkModeWrapper sectionName="community">
      <section className={`py-20 ${isDark ? 'bg-gradient-to-br from-slate-900 to-slate-800' : 'bg-gradient-to-br from-slate-50 to-blue-50'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Icon name="Globe" size={16} />
              <span>Community Impact</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Giving Back to the Tech Community
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Beyond coding, I'm passionate about nurturing the next generation of developers, 
              contributing to open-source projects, and building stronger tech communities.
            </p>
          </div>

          <div className="grid gap-8">
            {communityActivities.map((activity, index) => {
              const colors = getColorClasses(activity.color);
              
              return (
                <div key={activity.id} className={`${isDark ? 'bg-slate-800 border-gray-700' : 'bg-white border-border'} rounded-2xl border shadow-premium overflow-hidden community-card`}>
                  <div className="lg:flex">
                    {/* Image Section */}
                    <div className="lg:w-1/3">
                      <div className="h-64 lg:h-full overflow-hidden">
                        <Image
                          src={activity.image}
                          alt={activity.title}
                          className="w-full h-full object-cover hover-scale transition-premium"
                        />
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="lg:w-2/3 p-8">
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center`}>
                            <Icon name={activity.icon} size={24} className="text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-text-primary">{activity.title}</h3>
                            <p className="text-text-secondary">{activity.organization}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`px-2 py-1 ${colors.light} ${colors.text} text-xs rounded-full font-medium`}>
                                {activity.role}
                              </span>
                              <span className="text-text-muted text-xs">{activity.duration}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <p className="text-text-secondary mb-6 leading-relaxed">
                        {activity.description}
                      </p>

                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Impact Metrics */}
                        <div>
                          <h4 className="font-semibold text-text-primary mb-3 flex items-center">
                            <Icon name="BarChart3" size={18} className="mr-2 text-primary" />
                            Key Impact
                          </h4>
                          <div className="space-y-2">
                            {activity.impact.map((item, itemIndex) => (
                              <div key={itemIndex} className="flex items-center space-x-2">
                                <Icon name="CheckCircle" size={14} className="text-success flex-shrink-0" />
                                <span className="text-text-secondary text-sm">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Achievements */}
                        <div>
                          <h4 className="font-semibold text-text-primary mb-3 flex items-center">
                            <Icon name="Award" size={18} className="mr-2 text-warning" />
                            Achievements
                          </h4>
                          <div className="space-y-2">
                            {activity.achievements.map((achievement, achievementIndex) => (
                              <div key={achievementIndex} className="flex items-center space-x-2">
                                <Icon name="Star" size={14} className="text-warning flex-shrink-0" />
                                <span className="text-text-secondary text-sm">{achievement}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Community Stats */}
          <div className={`mt-16 ${isDark ? 'bg-slate-800 border-gray-700' : 'bg-white border-border'} rounded-2xl border shadow-premium p-8 community-card`}>
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-text-primary mb-2">Community Impact by Numbers</h3>
              <p className="text-text-secondary">Measurable contributions to the tech ecosystem</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="Users" size={28} className="text-primary" />
                </div>
                <div className="text-3xl font-bold text-primary mb-1">700+</div>
                <div className="text-sm text-text-muted">Developers Reached</div>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-success/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="GitBranch" size={28} className="text-success" />
                </div>
                <div className="text-3xl font-bold text-success mb-1">15+</div>
                <div className="text-sm text-text-muted">Open Source Projects</div>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-purple/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="MessageCircle" size={28} className="text-brand-purple" />
                </div>
                <div className="text-3xl font-bold text-brand-purple mb-1">12</div>
                <div className="text-sm text-text-muted">Conference Talks</div>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="GraduationCap" size={28} className="text-accent" />
                </div>
                <div className="text-3xl font-bold text-accent mb-1">25+</div>
                <div className="text-sm text-text-muted">Students Mentored</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </DarkModeWrapper>
  );
};

export default CommunityImpactSection;